import type { CSSProperties } from "react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { exchangeAdminLoginCode } from "@/api/adminWebAuth";
import { useSessionStore } from "@/store/session";
import { FullScreenLoader } from "@/components/FullScreenLoader";

const WRAP: CSSProperties = {
  minHeight: "100dvh",
  background: "var(--d-ink)",
  color: "var(--d-fg)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
};
const CARD: CSSProperties = {
  background: "var(--d-ink-2)",
  border: "1px solid var(--d-line)",
  borderRadius: 22,
  padding: 24,
  maxWidth: 420,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 16,
  textAlign: "center",
};
const INSTRUCTION: CSSProperties = {
  fontFamily: "var(--font-body)",
  fontSize: 14,
  color: "var(--d-fg-muted)",
  margin: 0,
};
const LINK: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: 48,
  marginTop: 8,
  borderRadius: 999,
  background: "var(--d-violet)",
  color: "#fff",
  textDecoration: "none",
  fontFamily: "var(--font-body)",
  fontSize: 14,
  fontWeight: 600,
};

/** DUE-221 (Task 12): redeems the one-time code from the bot's
 * `/admin_web` magic link (`?code=`) for a real session. Mounted at
 * `/auth` — the link the bot replies with points here. */
export function AuthCallbackPage(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation("admin");
  const [failed, setFailed] = useState(false);
  const code = params.get("code");
  useEffect(() => {
    if (!code) {
      setFailed(true);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const envelope = await exchangeAdminLoginCode(code);
        useSessionStore.getState().setSession(envelope);
        if (!cancelled) navigate("/admin/economics", { replace: true });
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, navigate]);
  if (failed) {
    return (
      <div style={WRAP}>
        <div style={CARD}>
          <p style={INSTRUCTION}>{t("web_login.error_invalid")}</p>
          <Link to="/login" style={LINK}>
            {t("web_login.back_to_login")}
          </Link>
        </div>
      </div>
    );
  }
  return <FullScreenLoader />;
}
